import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import CustomerFormModal from './CustomerFormModal';

const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomer = () => {
    setSelectedCustomer(null);
    setIsFormOpen(true);
  };

  const handleEditCustomer = (customer) => {
    setSelectedCustomer(customer);
    setIsFormOpen(true);
  };

  const handleDeleteCustomer = async (customerId, customerName) => {
    if (!window.confirm(`Delete customer "${customerName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/customers/${customerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchCustomers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete customer');
      }
    } catch (error) {
      console.error('Error deleting customer:', error);
      alert('Failed to delete customer');
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedCustomer(null);
    fetchCustomers();
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-center p-8">Loading customers...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[var(--accent)]">CUSTOMER MANAGEMENT</h2>
        <Button onClick={handleCreateCustomer} className="bg-green-600 hover:bg-green-700 text-white font-bold">
          [+ NEW CUSTOMER]
        </Button>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search customers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] placeholder-[var(--accent)]/50"
        />
      </div>

      {filteredCustomers.length === 0 ? (
        <div className="text-center p-8 text-[var(--accent)]/70">
          {searchTerm ? 'No customers found matching search' : 'No customers yet. Create one to get started.'}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredCustomers.map(customer => (
            <div
              key={customer.id}
              className="border-2 border-[var(--accent)] p-4 bg-black/50 hover:bg-black/70 transition-all"
            >
              <h3 className="text-xl font-bold text-[var(--accent)] mb-2">{customer.name}</h3>
              
              <div className="space-y-2 text-sm mb-4">
                {customer.phone && (
                  <div className="text-[var(--accent)]/80">
                    <span className="font-bold">Phone:</span> {customer.phone}
                  </div>
                )}
                {customer.email && (
                  <div className="text-[var(--accent)]/80">
                    <span className="font-bold">Email:</span> {customer.email}
                  </div>
                )}
                {customer.address && (
                  <div className="text-[var(--accent)]/80">
                    <span className="font-bold">Address:</span> {customer.address}
                  </div>
                )}
                {customer.wifi_ssid && (
                  <div className="text-[var(--accent)]/80">
                    <span className="font-bold">WiFi:</span> {customer.wifi_ssid}
                  </div>
                )}
                {customer.system_passwords && customer.system_passwords.length > 0 && (
                  <div className="text-[var(--accent)]/80">
                    <span className="font-bold">Passwords:</span> {customer.system_passwords.length} stored
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleEditCustomer(customer)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold"
                  size="sm"
                >
                  [VIEW/EDIT]
                </Button>
                <Button
                  onClick={() => handleDeleteCustomer(customer.id, customer.name)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold"
                  size="sm"
                >
                  [DELETE]
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFormOpen && (
        <CustomerFormModal
          customer={selectedCustomer}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedCustomer(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default AdminCustomers;
